/**
 * ResultScreen.tsx
 * Last updated: 2025-08-12 15:53:41
 * Updated by: JulianPolly
 */

import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { MentalAccountingKey } from "./MentalAccountingSequence";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, chartColors, commonStyles } from "../../../../components/colors";

// Colors for options - using primary chart colors
const choiceColors = chartColors.primary;

// Custom Tooltip with percentages
const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 470,
    maxWidth: 600,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 180,
    maxWidth: 300,
    flex: 1
};

export const ResultScreenComponent: React.FC = () => {
    const { getGroupData, userData } = useSession();
    const { t } = useLanguage();

    const [fall1Data, setFall1Data] = useState([
        {
            name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'yes'),
            Anzahl: 0,
            color: choiceColors[0]
        },
        {
            name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'no'),
            Anzahl: 0,
            color: choiceColors[1]
        }
    ]);

    const [fall2Data, setFall2Data] = useState([
        {
            name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'yes'),
            Anzahl: 0,
            color: choiceColors[0]
        },
        {
            name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'no'),
            Anzahl: 0,
            color: choiceColors[1]
        }
    ]);

    const [totals, setTotals] = useState({ fall1: 0, fall2: 0 });
    const [loading, setLoading] = useState(true);

    // Eigene Entscheidung und Bedingung robust ermitteln (unabhängig vom Index!)
    let eigeneEntscheidung: number | null = null;
    let eigeneBedingung: string | null = null;
    if (userData && userData[MentalAccountingKey]) {
        for (const result of Object.values(userData[MentalAccountingKey]) as any) {
            if (result && result.selections && result.selections.length > 0) {
                eigeneEntscheidung = result.selections[0].choice;
                eigeneBedingung = result.selections[0].bedingung ?? null;
                break;
            }
        }
    }

    // Satz für eigenen Fall und Entscheidung
    let groupLabel = eigeneBedingung === "Bedingung 2"
        ? t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'group2')
        : t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'group1');
    let fallLabel = eigeneBedingung === "Bedingung 2"
        ? t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'case2')
        : t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'case1');

    // Optionen-Texte basierend auf Bedingung und Auswahl
    let userDecision = t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'noDecision');

    if (eigeneEntscheidung !== null) {
        if (eigeneBedingung === "Bedingung 2") {
            userDecision = eigeneEntscheidung === 1
                ? t('experiments', 'mentalAccounting', 'screens', 'screen2', 'options', 'yes')
                : t('experiments', 'mentalAccounting', 'screens', 'screen2', 'options', 'no');
        } else {
            userDecision = eigeneEntscheidung === 1
                ? t('experiments', 'mentalAccounting', 'screens', 'screen1', 'options', 'yes')
                : t('experiments', 'mentalAccounting', 'screens', 'screen1', 'options', 'no');
        }
    }

    // HTML-Entitäten korrekt formatieren
    const formatContent = (htmlContent: string) => {
        return htmlContent.replace(/&nbsp;/g, ' ');
    };

    let infoText = eigeneEntscheidung !== null
        ? formatContent(t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'intro')
            .replace('{group}', groupLabel)
            .replace('{case}', fallLabel)) +
        ' ' + t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'decision') +
        ' <b>' + userDecision + '</b>'
        : t('experiments', 'mentalAccounting', 'screens', 'results', 'userInfo', 'noDecision');

    // Aggregierte Auswertung
    useEffect(() => {
        setLoading(true);
        getGroupData().then((data: any[]) => {
            let fall1Counts = [0, 0]; // [Ja, Nein]
            let fall2Counts = [0, 0]; // [Ja, Nein]
            data?.forEach((dat: any) => {
                if (dat[MentalAccountingKey]) {
                    for (const result of Object.values(dat[MentalAccountingKey]) as any) {
                        if (result?.selections?.length > 0) {
                            const selection = result.selections[0];
                            const choice = selection.choice;
                            const bedingung = selection.bedingung;

                            if (bedingung === "Bedingung 1") {
                                if (choice === 1) fall1Counts[0]++;
                                if (choice === 2) fall1Counts[1]++;
                            }
                            if (bedingung === "Bedingung 2") {
                                if (choice === 1) fall2Counts[0]++;
                                if (choice === 2) fall2Counts[1]++;
                            }
                        }
                    }
                }
            });

            const totalFall1 = fall1Counts[0] + fall1Counts[1];
            const totalFall2 = fall2Counts[0] + fall2Counts[1];

            setTotals({
                fall1: totalFall1,
                fall2: totalFall2
            });

            setFall1Data([
                { name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'yes'), Anzahl: fall1Counts[0], color: choiceColors[0] },
                { name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'no'), Anzahl: fall1Counts[1], color: choiceColors[1] }
            ]);

            setFall2Data([
                { name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'yes'), Anzahl: fall2Counts[0], color: choiceColors[0] },
                { name: t('experiments', 'mentalAccounting', 'screens', 'results', 'chartOptions', 'no'), Anzahl: fall2Counts[1], color: choiceColors[1] }
            ]);

            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData, t]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {t('experiments', 'mentalAccounting', 'screens', 'results', 'loading')}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t('experiments', 'mentalAccounting', 'screens', 'results', 'title')}
            </h2>

            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 48,
                marginTop: 28
            }}>
                {/* Fall 1 Card */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {t('experiments', 'mentalAccounting', 'screens', 'results', 'case1', 'title')}
                    </h3>
                    <p style={{
                        textAlign: "center",
                        fontSize: 16,
                        color: colors.textMedium,
                        marginBottom: 20
                    }}>
                        {t('experiments', 'mentalAccounting', 'screens', 'results', 'case1', 'description')}
                    </p>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            {/* Removed the "Decisions" heading here */}
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={fall1Data}>
                                    <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={totals.fall1} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {fall1Data.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Fall 2 Card */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {t('experiments', 'mentalAccounting', 'screens', 'results', 'case2', 'title')}
                    </h3>
                    <p style={{
                        textAlign: "center",
                        fontSize: 16,
                        color: colors.textMedium,
                        marginBottom: 20
                    }}>
                        {t('experiments', 'mentalAccounting', 'screens', 'results', 'case2', 'description')}
                    </p>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            {/* Removed the "Decisions" heading here */}
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={fall2Data}>
                                    <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={totals.fall2} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {fall2Data.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* User information message */}
            <div
                style={resultMessageStyle}
                dangerouslySetInnerHTML={{ __html: infoText }}>
            </div>
        </div>
    );
};

export const ResultScreen: ScreenConfig = {
    component: ResultScreenComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};